<template>
    <section class="link-preview-custom">
        <label>
            {{ $t('dash.post-creator.short-link-og.label') }}
            <ComponentSwitch
                :value="!!snippet.customActived"
                :onChange="v => update('customActived', v)"
                :id="'switch-link-preview-custom'"
            ></ComponentSwitch>
        </label>
        <p class="sub-label">{{ $t('dash.post-creator.short-link-og.subtitle') }}</p>

        <template v-if="snippet.customActived">
            <p class="warning">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{ $t('dash.post-creator.short-link-og.warning') }}
            </p>

            <input
                type="file"
                ref="inputImage"
                accept="image/*"
                hidden
                @change="selectImage"
            >

            <div class="og-image" @click="toggleImage">
                <template v-if="imageFile && imageFile.isUpload">
                    <img :src="imageFile.url" alt="">
                    <span>{{ imageFile.info && imageFile.info.name }}</span>
                    <i class="fa-regular fa-square-minus"></i>
                </template>
                <template v-else-if="snippet.image && imageFile && imageFile.failedUpload">
                    <i class="fa-solid fa-circle-exclamation"></i>
                    <span>{{ $t('dash.post-creator.short-link-og.image-failed') }}</span>
                </template>
                <template v-else-if="snippet.image">
                    <i class="fa-solid fa-spinner fa-spin"></i>
                    <span>{{ $t('dash.post-creator.short-link-og.image-uploading') }}</span>
                </template>
                <template v-else>
                    <i class="fa-solid fa-image"></i>
                    <span>{{ $t('dash.post-creator.short-link-og.image') }}</span>
                    <i class="fa-solid fa-plus"></i>
                </template>
            </div>

            <div class="og-input">
                <i class="fa-solid fa-heading"></i>
                <input
                    :placeholder="$t('dash.post-creator.short-link-og.title')"
                    maxlength="200"
                    :value="snippet.title"
                    @input="e => update('title', e.target.value)"
                >
            </div>

            <div class="og-input">
                <i class="fa-solid fa-align-left"></i>
                <input
                    :placeholder="$t('dash.post-creator.short-link-og.description')"
                    maxlength="300"
                    :value="snippet.description"
                    @input="e => update('description', e.target.value)"
                >
            </div>

            <p class="hint" v-if="!complete">{{ $t('dash.post-creator.short-link-og.incomplete') }}</p>
        </template>
    </section>
</template>

<script>
import { EventBus } from '@/event-bus'
import ComponentSwitch from '@/components/dash/utils/switch.vue'
import Posts from '@/components/mixins/Posts'
import { originalPreviewFromMeta, fileFromDataUri } from '../../linkPreview.js'

// Saisie unique de l'aperçu personnalisé du lien (affichée seulement avec la beta url_shortener ; sans
// la beta, l'UI historique de formLinkedin.vue reste en place). Écrit dans formDataNetwork.linkedin.snippet,
// cf. ../../linkPreview.js pour l'envoi (snippet/snippetTitle LinkedIn + short_link_og).
export default {
    name: 'LinkPreviewCustom',

    components: { ComponentSwitch },

    // getNewsMeta (GET /posts/meta, cache store), getImageFromUrl (GET /posts/image)
    mixins: [Posts],

    props: {
        formData: { type: Object, required: true },
        files: { type: Object, required: true }
    },

    computed: {
        snippet() {
            const linkedin = this.formData.formDataNetwork && this.formData.formDataNetwork.linkedin
            return (linkedin && linkedin.snippet) || {}
        },
        imageFile() {
            return this.snippet.image && this.snippet.image.id ? this.files[this.snippet.image.id] : null
        },
        complete() {
            return !!((this.snippet.title || '').trim() && this.imageFile && this.imageFile.isUpload)
        }
    },

    methods: {
        update(key, value) {
            this.$set(this.formData.formDataNetwork.linkedin.snippet, key, value)
            this.$emit('saveFormData')
            if (key === 'customActived' && value) this.prefillFromOriginal()
        },

        // Activation : champs vides pré-remplis avec l'aperçu d'origine du lien, pour ne modifier que le titre, l'image
        // ou la description. L'image d'origine est lue par l'engine (pas de CORS) puis envoyée sur S3 comme une image
        // ajoutée à la main : l'aperçu publié ne dépend jamais d'une image hébergée par le site.
        // Échec silencieux : les champs restent à saisir (titre et image requis, cf. hint « incomplete »).
        async prefillFromOriginal() {
            const link = this.snippet.link || (this.formData.urlsTextContentHistory || [])[0]
            if (!link || (this.snippet.title && this.snippet.description && this.snippet.image)) return
            try {
                const res = await this.getNewsMeta(link)
                const original = originalPreviewFromMeta(res && res.body)
                // L'utilisateur a pu désactiver ou saisir pendant l'appel : ne rien écraser
                if (!original || !this.snippet.customActived) return
                if (!this.snippet.title && original.title) this.update('title', original.title.slice(0, 200))
                if (!this.snippet.description && original.description) this.update('description', original.description.slice(0, 300))
                if (this.snippet.image || !original.imageUrl) return

                const image = await this.getImageFromUrl(encodeURIComponent(original.imageUrl))
                const file = await fileFromDataUri(image && image.body && image.body.image)
                if (!file || this.snippet.image || !this.snippet.customActived) return
                const newFileId = Math.random().toString(36).substr(2, 9)
                this.update('image', { id: newFileId })
                EventBus.$emit('postcreator/uploadFile', null, file, true, newFileId)
            } catch (error) {
                console.error('prefillFromOriginal', error)
            }
        },

        toggleImage() {
            if (this.snippet.image) {
                this.update('image', undefined)
            } else {
                this.$refs.inputImage.click()
            }
        },

        // Même upload que l'UI historique LinkedIn (formLinkedin.vue)
        selectImage(e) {
            const file = e.target.files && e.target.files[0]
            if (!file) return
            const newFileId = Math.random().toString(36).substr(2, 9)
            this.update('image', { id: newFileId })
            EventBus.$emit('postcreator/uploadFile', null, file, true, newFileId)
            e.target.value = ''
        }
    }
}
</script>

<style scoped>
.link-preview-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.link-preview-custom label {
    display: flex;
    align-items: center;
    gap: 8px;
}
.link-preview-custom label .switch {
    margin-left: auto;
}
.warning {
    margin: 0;
    font-size: 12px;
    color: #8a5a00;
    background: #fff6e0;
    border: 1px solid #f5d98b;
    border-radius: 8px;
    padding: 8px 10px;
    display: flex;
    gap: 8px;
    align-items: flex-start;
}
.hint {
    margin: 0;
    font-size: 12px;
    color: #8a8fa3;
}
.og-image,
.og-input {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1.5px solid #e0d0ea;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 13px;
    color: #2F1937;
}
.og-image {
    cursor: pointer;
}
.og-image img {
    width: 48px;
    height: 25px;
    object-fit: cover;
    border-radius: 4px;
}
.og-image span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.og-image i,
.og-input i {
    color: #6B1F4D;
}
.og-input input {
    border: none;
    outline: none;
    flex: 1;
    min-width: 0;
    font-size: 13px;
}
</style>
